import * as echarts from 'echarts';
import { get, keyBy } from 'lodash/fp';
import { useEffect, useState, useRef, useMemo } from 'react';
import { SizeMe } from 'react-sizeme';
import styled from 'styled-components';
import { type Datum } from '../shared/typings';

export type GroupedChartProps = {
    data: Datum; // label as key, e.g. Current Month (Mar)
    xField: string;
    xAxisTitle: string;
    yField: string;
    yAxisTitle: string;
    visible: Record<string, boolean>; // label as key, e.g. Current Month (Mar)
    color: Record<string, string>; // label as key, e.g. Current Month (Mar)
    mode: 'bar' | 'column';
};

type InnerProps = GroupedChartProps & { size: { width: number; height: number } };

const Container = styled.div`
    width: 100%;
    height: 100%;
    min-height: 400px;
`;

const Inner = ({ data, xField, xAxisTitle, yField, yAxisTitle, visible, color, size, mode }: InnerProps) => {
    const element = useRef<HTMLElement | null>(null);
    const [mounted, setMounted] = useState(false);
    const [chart, setChart] = useState<echarts.ECharts | null>(null);

    useEffect(() => {
        if (mounted) {
            setChart(lastChart => {
                lastChart?.dispose();

                if (size.width > 0 && size.height > 0) {
                    return echarts.init(element.current, null, { width: size.width, height: size.height });
                }

                return null;
            });
        }
    }, [mounted, size]);

    const option = useMemo(() => {
        const common = {
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow',
                },
            },
            legend: {},
            color: Object.keys(data).map(key => color[key]),
            grid: { containLabel: true },
        };

        let categoryField: string | undefined;
        let valueField: string | undefined;
        switch (mode) {
            case 'column': {
                categoryField = xField;
                valueField = yField;

                break;
            }

            case 'bar': {
                categoryField = yField;
                valueField = xField;

                break;
            }

            default:
                throw new Error('invalid mode');
        }

        const getCategory = get(categoryField);
        const categories = Array.from(new Set(Object.values(data).flat().map(getCategory))).toSorted();

        const getValue = get(valueField);
        const series = Object.entries(data)
            .filter(([key]) => visible[key])
            .map(([key, items]) => {
                const itemByCategory = keyBy(categoryField, items);

                return { type: 'bar', name: key, data: categories.map(category => getValue(itemByCategory[category])) };
            });

        switch (mode) {
            case 'column': {
                return {
                    ...common,
                    series,
                    xAxis: {
                        type: 'category',
                        data: categories,
                        name: xAxisTitle,
                        nameLocation: 'middle',
                        nameTextStyle: {
                            padding: [10, 0, 0, 0],
                        },
                    },
                    yAxis: {
                        type: 'value',
                        name: yAxisTitle,
                        nameLocation: 'middle',
                        nameTextStyle: {
                            padding: [0, 0, 30, 0],
                        },
                    },
                    dataZoom: [{ type: 'slider', show: true, showDataShadow: false, orient: 'horizontal', height: 15 }],
                };
            }

            case 'bar': {
                return {
                    ...common,
                    series,
                    xAxis: {
                        type: 'value',
                        name: xAxisTitle,
                        nameLocation: 'middle',
                        nameTextStyle: {
                            padding: [10, 0, 0, 0],
                        },
                    },
                    yAxis: {
                        type: 'category',
                        data: categories,
                        name: yAxisTitle,
                        nameLocation: 'middle',
                        nameTextStyle: {
                            padding: [0, 0, 30, 0],
                        },
                    },
                    dataZoom: [{ type: 'slider', show: true, showDataShadow: false, orient: 'vertical', width: 15 }],
                };
            }

            default:
                throw new Error('invalid mode');
        }
    }, [mode, xField, data, yField, xAxisTitle, yAxisTitle, visible, color]);

    useEffect(() => {
        chart?.setOption(option, true);
    }, [option, chart]);

    // dispose the chart when umount
    useEffect(() => {
        setMounted(true);

        return () => {
            chart?.dispose();
        };
    }, []);

    return (
        <Container>
            <div
                ref={ref => {
                    element.current = ref;
                }}
            />
        </Container>
    );
};

const GroupedChart = (props: GroupedChartProps) => (
    <SizeMe refreshRate={1000} monitorHeight>
        {({ size }) => <Inner size={size} {...props} />}
    </SizeMe>
);

export default GroupedChart;
